import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { TbShoppingCart, TbX } from 'react-icons/tb';
import clsx from 'clsx';
import type { QuickAddProduct, QuickAddToCartProps } from './types';
import QuickAddForm from './QuickAddForm';
import './QuickAddToCart.scss';

function track(event: string, product: QuickAddProduct) {
  const { dataLayer } = window as Window & { dataLayer?: object[] };
  dataLayer?.push({
    event,
    ecommerce: {
      items: [
        { item_name: product.name, item_id: product.id, price: product.price },
      ],
    },
  });
}

export default function QuickAddToCart({
  controlId,
  action,
  product,
  variant,
  price,
  originalPrice,
}: QuickAddToCartProps) {
  const [open, setOpen] = useState(false);
  const contentId = `${controlId}-popover`;
  const triggerClass = clsx(
    'btn-quick-add-to-cart bio-quick-add-control__trigger',
    { 'purchase-price': variant === 'card' },
  );
  const label = (
    <>
      <TbShoppingCart aria-hidden="true" />{' '}
      {originalPrice && (
        <span
          className={
            variant === 'card' ? 'discounted-light-background' : 'discounted'
          }
        >
          {originalPrice}
        </span>
      )}{' '}
      {price && <span className="price">{price}</span>}
    </>
  );

  function onOpenChange(next: boolean) {
    track(next ? 'quick_add_to_cart_open' : 'quick_add_to_cart_close', product);
    setOpen(next);
  }

  return (
    <div className={`bio-quick-add-control bio-quick-add-control--${variant}`}>
      {product.productLink ? (
        <a
          className={triggerClass}
          href={product.productLink}
          aria-label={price ? `Add to cart, ${price}` : 'Add to cart'}
        >
          {label}
        </a>
      ) : (
        <Popover.Root open={open} onOpenChange={onOpenChange}>
          <Popover.Trigger
            className={triggerClass}
            data-product-id={product.id}
            aria-label={price ? `Add to cart, ${price}` : 'Add to cart'}
            aria-controls={open ? contentId : undefined}
          >
            {label}
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              id={contentId}
              className="bio-quick-add"
              side="bottom"
              collisionPadding={16}
              onOpenAutoFocus={(event) => event.preventDefault()}
              onCloseAutoFocus={(event) => {
                // Keep focus in another control when switching popovers.
                if (document.activeElement !== document.body)
                  event.preventDefault();
              }}
            >
              <QuickAddForm
                action={action}
                product={product}
                quantityId={`${controlId}-quantity`}
                onAdded={() => setOpen(false)}
              />
              <Popover.Close
                className="bio-quick-add__close"
                aria-label="Close"
              >
                <TbX size={16} />
              </Popover.Close>
              <Popover.Arrow
                className="bio-quick-add__arrow"
                width={20}
                height={10}
              />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}
    </div>
  );
}
